/**
 * Derives metric-grid applicability for one promo instance from the FE-authored
 * `metricRules` registry (target-only `metric:<key>` scope) and the store's
 * cross-section values.
 *
 * `metricRules` stay OUT of the store (they are pure, derived, never written, and
 * non-reactive); this hook reads the same merged per-instance view as
 * `useScopeFieldStates('promo', ..., instanceId)` (global event/plan values +
 * this tab's promo values, ctx + the instance's `hasEntityId`) so a metric's
 * applicability can depend on any field in any section.
 *
 * Returns a map of metricKey -> applicable for every metric that HAS a rule.
 * Metrics without a rule are absent from the map; callers MUST treat absence as
 * applicable (`map[key] ?? true`).
 */
import { useMemo } from 'react';
import { useStore } from 'zustand';

import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { deriveFieldStatesForScope } from '../engine/deriveFieldStates';
import { useRuleStoreApi } from './FieldRulesProvider';

export function useMetricApplicability(instanceId: string): Record<string, boolean> {
    const store = useRuleStoreApi();
    const { metricRules } = useTpoGenConfig();
    const values = useStore(store, (s) => s.values);
    const ctx = useStore(store, (s) => s.ctx);
    const instance = useStore(store, (s) => s.promo[instanceId]);

    return useMemo(() => {
        const merged = { ...values, ...(instance?.values ?? {}) };
        const mergedCtx = { ...ctx, hasEntityId: instance?.hasEntityId, isFirstPromo: instance?.isFirstPromo };
        const states = deriveFieldStatesForScope(metricRules, 'metric', merged, mergedCtx);
        const applicableByKey: Record<string, boolean> = {};
        for (const key of Object.keys(states)) applicableByKey[key] = states[key].applicable;
        return applicableByKey;
    }, [metricRules, values, ctx, instance]);
}
